import { FC, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions';
import { PaymentCardType } from '@shinetools/pricing-plan-library';

import {
  ApprovePhysicalPaymentCardOrderRequestInput,
  TeamMembersRequestStatus,
  TeamMembersRequestType,
} from '__generated__/GQL';
import DetailSidebar from 'components/DetailSidebar';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { CreditCardsDocument } from 'features/PaymentCards/hooks/useCreditCards/creditCards.gql';
import { OngoingPaymentCardsRequestsDocument } from 'features/PaymentCards/hooks/useOngoingPaymentCardsRequests/ongoingPaymentCardsRequests.gql';
import { ApproveVirtualBudgetCrdRequestDocument } from 'features/Team/pages/Requests/graphql/approveVirtualBudgetCardRequest.gql';

import { ApprovePhysicalPaymentCardRequestDocument } from '../../graphql/approvePhysicalPaymentCardRequest.gql';
import { ApproveVirtualPaymentCardRequestDocument } from '../../graphql/approveVirtualPaymentCardRequest.gql';
import { RejectRequestDocument } from '../../graphql/rejectRequest.gql';
import {
  isVirtualBudgetCardRequest,
  PaymentCardRequest,
} from '../../hooks/useRequests';
import ArchivedRequestSummary from './components/ArchivedRequestSummary';
import PhysicalPaymentCardRequestForm from './components/PhysicalPaymentCardRequestForm';
import RequestApproved from './components/RequestApproved';
import RequestRejected from './components/RequestRejected';
import VirtualPaymentCardRequestForm from './components/VirtualPaymentCardRequestForm';
import { VirtualPaymentCardRequestFormData } from './components/VirtualPaymentCardRequestForm/types';
import { usePaymentCardRequestFees } from './hooks/usePaymentCardRequestFees';
import { usePhysicalPaymentCardLimits } from './hooks/usePhysicalPaymentCardLimits';
import { useVirtualPaymentCardLimits } from './hooks/useVirtualPaymentCardLimits';

interface PaymentCardRequestSidebarProps {
  bankAccountId: string;
  companyProfileId: string;
  isOnPendingFilter: boolean;
  onClose?: () => void;
  request: PaymentCardRequest;
}

const PaymentCardRequestSidebar: FC<PaymentCardRequestSidebarProps> = ({
  bankAccountId,
  companyProfileId,
  isOnPendingFilter,
  onClose,
  request,
}) => {
  const physicalPaymentCardLimits = usePhysicalPaymentCardLimits(
    PaymentCardType.BusinessWorldDebitCard,
  );

  const virtualPaymentCardLimits = useVirtualPaymentCardLimits();

  const { teamMembersRequestId } = request;
  const { refetch: refetchFees } = usePaymentCardRequestFees(request.type);

  const refetchPaymentCardsQueriesParams: MutationBaseOptions = {
    awaitRefetchQueries: true,
    refetchQueries: [CreditCardsDocument, OngoingPaymentCardsRequestsDocument],
  };

  const [rejectRequestMutation, rejectRequestHook] = useMutation(
    RejectRequestDocument,
  );

  const [
    approvePhysicalPaymentCardRequestMutation,
    approvePhysicalPaymentCardRequestHook,
  ] = useMutation(ApprovePhysicalPaymentCardRequestDocument);

  const [
    approveVirtualPaymentCardRequestMutation,
    approveVirtualPaymentCardRequestHook,
  ] = useMutation(ApproveVirtualPaymentCardRequestDocument);

  const [
    approveVirtualBudgetCardRequestMutation,
    approveVirtualBudgetCardRequestHook,
  ] = useMutation(ApproveVirtualBudgetCrdRequestDocument);

  const rejectRequest = () =>
    rejectRequestMutation({
      ...refetchPaymentCardsQueriesParams,
      variables: {
        input: {
          companyProfileId,
          teamMembersRequestId,
        },
      },
    });

  const approvePhysicalPaymentCardRequest = (
    formData: Omit<
      ApprovePhysicalPaymentCardOrderRequestInput,
      'bankAccountId' | 'companyProfileId' | 'teamMembersRequestId'
    >,
  ) =>
    approvePhysicalPaymentCardRequestMutation({
      ...refetchPaymentCardsQueriesParams,
      variables: {
        input: {
          ...formData,
          bankAccountId,
          companyProfileId,
          teamMembersRequestId,
        },
      },
    });

  const approveVirtualPaymentCardRequest = ({
    deactivateAt,
    limitPaymentAll,
    ...formData
  }: VirtualPaymentCardRequestFormData) => {
    const input = {
      ...formData,
      bankAccountId,
      companyProfileId,
      /**
       * `undefined` is not a valid resolver value and will be stripped of the input
       * we have to use `null` instead
       */
      deactivateAt: deactivateAt === undefined ? null : deactivateAt,
      teamMembersRequestId,
    };

    if (isVirtualBudgetCardRequest(request) && limitPaymentAll) {
      return approveVirtualBudgetCardRequestMutation({
        ...refetchPaymentCardsQueriesParams,
        variables: {
          input: {
            ...input,
            limitPaymentAll,
          },
        },
      });
    }

    return approveVirtualPaymentCardRequestMutation({
      ...refetchPaymentCardsQueriesParams,
      variables: {
        input,
      },
    });
  };

  const isPhysicalPaymentCardRequest =
    request.__typename === 'PhysicalPaymentCardOrderRequest';

  const isRequestArchivedOrProcessing =
    request.status !== TeamMembersRequestStatus.Pending;

  const isRequestPending = request.status === TeamMembersRequestStatus.Pending;

  useEffect(() => {
    refetchFees({
      isVirtual:
        request.type === TeamMembersRequestType.VirtualPaymentCardOrder,
    });
  }, [refetchFees, request]);

  if (
    virtualPaymentCardLimits.loading ||
    !virtualPaymentCardLimits.limits ||
    physicalPaymentCardLimits.loading ||
    !physicalPaymentCardLimits.limits
  ) {
    return (
      <DetailSidebar variant="ghost">
        <DetailSidebar.Header />
        <DetailSidebar.Body paddingX="space-16">
          <Loader />
        </DetailSidebar.Body>
      </DetailSidebar>
    );
  }

  if (
    physicalPaymentCardLimits.error ||
    virtualPaymentCardLimits.error ||
    rejectRequestHook.error ||
    approvePhysicalPaymentCardRequestHook.error ||
    approveVirtualBudgetCardRequestHook.error ||
    approveVirtualPaymentCardRequestHook.error
  ) {
    return <ErrorCard />;
  }

  if (
    isOnPendingFilter &&
    request.status === TeamMembersRequestStatus.Rejected
  ) {
    return (
      <DetailSidebar onClose={onClose} variant="ghost">
        <DetailSidebar.Header />
        <DetailSidebar.Body paddingX="space-16">
          <RequestRejected request={request} />
        </DetailSidebar.Body>
      </DetailSidebar>
    );
  }

  if (
    isOnPendingFilter &&
    request.status === TeamMembersRequestStatus.Approved
  ) {
    return (
      <DetailSidebar onClose={onClose} variant="ghost">
        <DetailSidebar.Header />
        <DetailSidebar.Body paddingX="space-16">
          <RequestApproved request={request} />
        </DetailSidebar.Body>
      </DetailSidebar>
    );
  }

  return (
    <DetailSidebar onClose={onClose} variant="ghost">
      <DetailSidebar.Header />
      {isRequestArchivedOrProcessing ? (
        <DetailSidebar.Body paddingX="space-16">
          <ArchivedRequestSummary request={request} />
        </DetailSidebar.Body>
      ) : null}
      {isRequestPending ? (
        <>
          {isPhysicalPaymentCardRequest ? (
            <PhysicalPaymentCardRequestForm
              handleApproveRequest={approvePhysicalPaymentCardRequest}
              handleRejectRequest={rejectRequest}
              isApprovingRequest={approvePhysicalPaymentCardRequestHook.loading}
              isRejectingRequest={rejectRequestHook.loading}
              limits={physicalPaymentCardLimits.limits}
              request={request}
            />
          ) : (
            <VirtualPaymentCardRequestForm
              handleApproveRequest={approveVirtualPaymentCardRequest}
              handleRejectRequest={rejectRequest}
              isApprovingRequest={
                approveVirtualPaymentCardRequestHook.loading ||
                approveVirtualBudgetCardRequestHook.loading
              }
              isRejectingRequest={rejectRequestHook.loading}
              limits={virtualPaymentCardLimits.limits}
              request={request}
            />
          )}
        </>
      ) : null}
    </DetailSidebar>
  );
};

export default PaymentCardRequestSidebar;
